export interface PlateReading {
  plateNo: number;
  serialNo: string;
  cfu: number | string;
  morphology?: string;
  interimRemarks?: string;
  readingTimestamp?: string;
}

export interface SpecifiedOrganismCheck {
  organismName: string;
  selectiveMedium: string;
  incubationCondition: string;
  colonyObservation: string;
  result: 'Absent' | 'Present' | 'Not Detected' | 'Pending';
  complianceStatus: 'Complies' | 'Does Not Comply' | 'Pending';
}

export interface MLTLabTestRecord {
  id: string; // Internal key e.g. 'lab-test-1'
  sNo: number;
  mltTestId: string; // e.g. 'MLT-26-27-000123'
  sampleId: string; // e.g. 'SAM-26-27-000456'
  sampleType: 'Finished Product' | 'Raw Material' | 'In-process Material' | 'Water Sample';
  productOrMaterial: string; // e.g. 'Product A'
  testParameter: 'TAMC' | 'TYMC' | 'Specified Microorganisms';
  medium: string; // e.g. 'SCDA (TSA)', 'SDA'
  numberOfPlates: number; // e.g. 5
  status: 'Pending' | 'In Progress' | 'Completed';
  assignedDate: string; // e.g. '10-Oct-2026'

  // Detailed Traceability & Metadata
  batchNo: string;
  productCode: string;
  arNumber: string;
  productionSchedule: string;
  scheduleLine: string;
  assignedAnalyst: string;
  samplingDate: string;
  samplingLocation: string;
  quantitySampled: string;
  quantityUsedForTest: string;
  testMethod: 'Spread Plate' | 'Pour Plate' | 'Membrane Filtration';
  dilutionFactor: string; // e.g. '1:10', '1:100', 'Direct (Undiluted)'
  diluentType: string;

  // Status-Specific: Incubation & Environment
  incubatorChamber?: string;
  targetIncubationTemp: string; // e.g. '30°C – 35°C' or '20°C – 25°C'
  actualIncubationTemp?: string; // e.g. '32.4°C'
  incubationDuration: string; // e.g. '3 to 5 Days' or '5 to 7 Days'
  incubationStartTime?: string;
  expectedReadingDate?: string;
  completionDate?: string;

  // Status-Specific: Plate Readings & Results
  plateReadings: PlateReading[];
  negativeControlCfu: number; // 0 for valid
  positiveControlVerified: boolean; // true
  averageCfuPerPlate?: number;
  calculatedBioburden?: string; // e.g. '190 CFU/g' or '< 10 CFU/ml'
  acceptanceLimit: string; // e.g. 'NMT 1000 CFU/g' or 'NMT 100 CFU/ml'
  complianceStatus?: 'Pass' | 'Fail' | 'Pending';

  // Specified Organisms (when relevant)
  specifiedOrganisms?: SpecifiedOrganismCheck[];

  // Audit & Remarks
  remarks: string;
  analystSignOff?: {
    name: string;
    role: string;
    signedAt: string;
  };
  qcReviewSignOff?: {
    name: string;
    role: string;
    status: 'Approved' | 'Pending QA Review';
    reviewedAt?: string;
  };
}

export const INITIAL_MLT_LAB_TEST_RECORDS: MLTLabTestRecord[] = [
  // 1. Pending (To Be Tested) - Row 1 in screenshot
  {
    id: 'lab-1',
    sNo: 1,
    mltTestId: 'MLT-26-27-000123',
    sampleId: 'SAM-26-27-000456',
    sampleType: 'Finished Product',
    productOrMaterial: 'Product A',
    testParameter: 'TAMC',
    medium: 'SCDA (TSA)',
    numberOfPlates: 5,
    status: 'Pending',
    assignedDate: '10-Oct-2026',
    batchNo: 'BAT-PRDA-261001',
    productCode: 'PRD-TAB-100',
    arNumber: 'AR/26-27/MLT/0488',
    productionSchedule: 'SCH-2026-0961',
    scheduleLine: 'Line 1 - Solid Oral Dosage Suite',
    assignedAnalyst: 'Dr. Sarah Jenkins',
    samplingDate: '09-Oct-2026 14:30',
    samplingLocation: 'Warehouse Sampling Booth B',
    quantitySampled: '50 g',
    quantityUsedForTest: '10 g',
    testMethod: 'Pour Plate',
    dilutionFactor: '1:10 (10g in 90ml Buffered Peptone Water)',
    diluentType: 'Buffered Peptone Water pH 7.0',
    targetIncubationTemp: '30.0°C – 35.0°C',
    incubationDuration: '3 to 5 Days (72h – 120h)',
    plateReadings: [
      { plateNo: 1, serialNo: 'PLT-TSA-261010-01', cfu: '—' },
      { plateNo: 2, serialNo: 'PLT-TSA-261010-02', cfu: '—' },
      { plateNo: 3, serialNo: 'PLT-TSA-261010-03', cfu: '—' },
      { plateNo: 4, serialNo: 'PLT-TSA-261010-04', cfu: '—' },
      { plateNo: 5, serialNo: 'PLT-TSA-261010-05', cfu: '—' }
    ],
    negativeControlCfu: 0,
    positiveControlVerified: true,
    acceptanceLimit: 'NMT 1000 CFU/g (USP <1111>)',
    remarks: 'Sample received in sealed amber glass container. Integrity intact.'
  },

  // 2. Pending (To Be Tested) - Row 2 in screenshot
  {
    id: 'lab-2',
    sNo: 2,
    mltTestId: 'MLT-26-27-000123',
    sampleId: 'SAM-26-27-000456',
    sampleType: 'Finished Product',
    productOrMaterial: 'Product A',
    testParameter: 'TYMC',
    medium: 'SDA',
    numberOfPlates: 5,
    status: 'Pending',
    assignedDate: '10-Oct-2026',
    batchNo: 'BAT-PRDA-261001',
    productCode: 'PRD-TAB-100',
    arNumber: 'AR/26-27/MLT/0489',
    productionSchedule: 'SCH-2026-0961',
    scheduleLine: 'Line 1 - Solid Oral Dosage Suite',
    assignedAnalyst: 'Dr. Sarah Jenkins',
    samplingDate: '09-Oct-2026 14:30',
    samplingLocation: 'Warehouse Sampling Booth B',
    quantitySampled: '50 g',
    quantityUsedForTest: '10 g',
    testMethod: 'Pour Plate',
    dilutionFactor: '1:10 (10g in 90ml Buffered Peptone Water)',
    diluentType: 'Buffered Peptone Water pH 7.0',
    targetIncubationTemp: '20.0°C – 25.0°C',
    incubationDuration: '5 to 7 Days (120h – 168h)',
    plateReadings: [
      { plateNo: 1, serialNo: 'PLT-SDA-261010-01', cfu: '—' },
      { plateNo: 2, serialNo: 'PLT-SDA-261010-02', cfu: '—' },
      { plateNo: 3, serialNo: 'PLT-SDA-261010-03', cfu: '—' },
      { plateNo: 4, serialNo: 'PLT-SDA-261010-04', cfu: '—' },
      { plateNo: 5, serialNo: 'PLT-SDA-261010-05', cfu: '—' }
    ],
    negativeControlCfu: 0,
    positiveControlVerified: true,
    acceptanceLimit: 'NMT 100 CFU/g (USP <1111>)',
    remarks: 'Awaiting incubation chamber assignment at 22.5°C.'
  },

  // 3. In Progress - Row 3 in screenshot
  {
    id: 'lab-3',
    sNo: 3,
    mltTestId: 'MLT-26-27-000124',
    sampleId: 'SAM-26-27-000457',
    sampleType: 'Raw Material',
    productOrMaterial: 'API Material A',
    testParameter: 'TAMC',
    medium: 'SCDA (TSA)',
    numberOfPlates: 3,
    status: 'In Progress',
    assignedDate: '10-Oct-2026',
    batchNo: 'LOT-APIA-8821',
    productCode: 'RAW-API-002',
    arNumber: 'AR/26-27/MLT/0490',
    productionSchedule: 'SCH-2026-0962',
    scheduleLine: 'QC Raw Material Testing Station 3',
    assignedAnalyst: 'Michael Chen',
    samplingDate: '10-Oct-2026 09:15',
    samplingLocation: 'Dispensing Booth A',
    quantitySampled: '25 g',
    quantityUsedForTest: '5 g',
    testMethod: 'Membrane Filtration',
    dilutionFactor: '1:100 (5g in 500ml 0.1% Peptone with Polysorbate 80)',
    diluentType: 'Peptone Water with 0.1% Tween 80',
    incubatorChamber: 'Incubator INC-MB-02 (Chamber A)',
    targetIncubationTemp: '30.0°C – 35.0°C',
    actualIncubationTemp: '32.3°C',
    incubationDuration: '3 to 5 Days (72h – 120h)',
    incubationStartTime: '10-Oct-2026 11:30 AM',
    expectedReadingDate: '13-Oct-2026 11:30 AM',
    plateReadings: [
      { plateNo: 1, serialNo: 'PLT-TSA-261010-11', cfu: 12, morphology: 'Pinpoint round white colonies', readingTimestamp: '12-Oct-2026 10:00 AM' },
      { plateNo: 2, serialNo: 'PLT-TSA-261010-12', cfu: 15, morphology: 'Pinpoint round white colonies', readingTimestamp: '12-Oct-2026 10:00 AM' },
      { plateNo: 3, serialNo: 'PLT-TSA-261010-13', cfu: 14, morphology: 'Pinpoint round white colonies', readingTimestamp: '12-Oct-2026 10:00 AM' }
    ],
    negativeControlCfu: 0,
    positiveControlVerified: true,
    averageCfuPerPlate: 13.7,
    calculatedBioburden: '~137 CFU/g (Interim 48h count)',
    acceptanceLimit: 'NMT 1000 CFU/g (USP <61>)',
    complianceStatus: 'Pending',
    remarks: '48h interim inspection completed. Colonies visible, no swarming observed. Final count due in 24h.'
  },

  // 4. Pending (To Be Tested) - Row 4 in screenshot
  {
    id: 'lab-4',
    sNo: 4,
    mltTestId: 'MLT-26-27-000125',
    sampleId: 'SAM-26-27-000458',
    sampleType: 'Raw Material',
    productOrMaterial: 'Excipient A',
    testParameter: 'TYMC',
    medium: 'SDA',
    numberOfPlates: 3,
    status: 'Pending',
    assignedDate: '11-Oct-2026',
    batchNo: 'LOT-EXCA-9930',
    productCode: 'RAW-EXC-014',
    arNumber: 'AR/26-27/MLT/0491',
    productionSchedule: 'SCH-2026-0963',
    scheduleLine: 'QC Raw Material Testing Station 2',
    assignedAnalyst: 'Dr. Sarah Jenkins',
    samplingDate: '10-Oct-2026 16:00',
    samplingLocation: 'Warehouse Sampling Booth A',
    quantitySampled: '30 g',
    quantityUsedForTest: '10 g',
    testMethod: 'Pour Plate',
    dilutionFactor: '1:10 (10g in 90ml Fluid Soybean-Casein Digest)',
    diluentType: 'Phosphate Buffer pH 7.2',
    targetIncubationTemp: '20.0°C – 25.0°C',
    incubationDuration: '5 to 7 Days (120h – 168h)',
    plateReadings: [
      { plateNo: 1, serialNo: 'PLT-SDA-261011-01', cfu: '—' },
      { plateNo: 2, serialNo: 'PLT-SDA-261011-02', cfu: '—' },
      { plateNo: 3, serialNo: 'PLT-SDA-261011-03', cfu: '—' }
    ],
    negativeControlCfu: 0,
    positiveControlVerified: true,
    acceptanceLimit: 'NMT 100 CFU/g (USP <1111>)',
    remarks: 'Sample pre-treatment completed. Ready for pouring and incubation.'
  },

  // 5. Pending (To Be Tested) - Row 5 in screenshot
  {
    id: 'lab-5',
    sNo: 5,
    mltTestId: 'MLT-26-27-000126',
    sampleId: 'SAM-26-27-000459',
    sampleType: 'In-process Material',
    productOrMaterial: 'Granules A',
    testParameter: 'TAMC',
    medium: 'SCDA (TSA)',
    numberOfPlates: 5,
    status: 'Pending',
    assignedDate: '11-Oct-2026',
    batchNo: 'BAT-GRA-261011',
    productCode: 'INP-GRA-102',
    arNumber: 'AR/26-27/MLT/0492',
    productionSchedule: 'SCH-2026-0964',
    scheduleLine: 'Granulation Line 3 - Suite 102',
    assignedAnalyst: 'Robert Taylor',
    samplingDate: '11-Oct-2026 08:30',
    samplingLocation: 'Granulation Dryer Discharge Port',
    quantitySampled: '50 g',
    quantityUsedForTest: '10 g',
    testMethod: 'Spread Plate',
    dilutionFactor: '1:10 (10g in 90ml Peptone Water)',
    diluentType: 'Buffered Peptone Water pH 7.0',
    targetIncubationTemp: '30.0°C – 35.0°C',
    incubationDuration: '3 to 5 Days (72h – 120h)',
    plateReadings: [
      { plateNo: 1, serialNo: 'PLT-TSA-261011-21', cfu: '—' },
      { plateNo: 2, serialNo: 'PLT-TSA-261011-22', cfu: '—' },
      { plateNo: 3, serialNo: 'PLT-TSA-261011-23', cfu: '—' },
      { plateNo: 4, serialNo: 'PLT-TSA-261011-24', cfu: '—' },
      { plateNo: 5, serialNo: 'PLT-TSA-261011-25', cfu: '—' }
    ],
    negativeControlCfu: 0,
    positiveControlVerified: true,
    acceptanceLimit: 'NMT 1000 CFU/g (In-process Specification)',
    remarks: 'Moisture content 1.8%. Homogenized with Stomacher for 2 minutes.'
  },

  // 6. Pending (To Be Tested) - Row 6 in screenshot
  {
    id: 'lab-6',
    sNo: 6,
    mltTestId: 'MLT-26-27-000127',
    sampleId: 'SAM-26-27-000460',
    sampleType: 'Water Sample',
    productOrMaterial: 'Purified Water',
    testParameter: 'TAMC',
    medium: 'SCDA (TSA)',
    numberOfPlates: 2,
    status: 'Pending',
    assignedDate: '12-Oct-2026',
    batchNo: 'WTR-PW-261012',
    productCode: 'UTL-WTR-001',
    arNumber: 'AR/26-27/MLT/0493',
    productionSchedule: 'SCH-2026-0965',
    scheduleLine: 'Loop 1 - Return Sampling Valve W-104',
    assignedAnalyst: 'Michael Chen',
    samplingDate: '12-Oct-2026 07:45',
    samplingLocation: 'Purified Water Generation Room',
    quantitySampled: '500 ml',
    quantityUsedForTest: '100 ml',
    testMethod: 'Membrane Filtration',
    dilutionFactor: 'Direct (100ml through 0.45µm membrane)',
    diluentType: 'None (Direct filtration)',
    targetIncubationTemp: '30.0°C – 35.0°C',
    incubationDuration: '5 Days (120h)',
    plateReadings: [
      { plateNo: 1, serialNo: 'PLT-TSA-261012-01', cfu: '—' },
      { plateNo: 2, serialNo: 'PLT-TSA-261012-02', cfu: '—' }
    ],
    negativeControlCfu: 0,
    positiveControlVerified: true,
    acceptanceLimit: 'NMT 100 CFU/ml (USP Monograph for Purified Water)',
    remarks: 'Sample flushed for 2 minutes prior to collection. Tested within 2 hours of sampling.'
  },

  // 7. Completed - Row 7 in screenshot
  {
    id: 'lab-7',
    sNo: 7,
    mltTestId: 'MLT-26-27-000128',
    sampleId: 'SAM-26-27-000461',
    sampleType: 'Finished Product',
    productOrMaterial: 'Product B',
    testParameter: 'TAMC',
    medium: 'SCDA (TSA)',
    numberOfPlates: 5,
    status: 'Completed',
    assignedDate: '12-Oct-2026',
    batchNo: 'BAT-PRDB-261005',
    productCode: 'PRD-CAP-250',
    arNumber: 'AR/26-27/MLT/0494',
    productionSchedule: 'SCH-2026-0966',
    scheduleLine: 'Line 4 - Capsule Encapsulation Suite',
    assignedAnalyst: 'Dr. Sarah Jenkins',
    samplingDate: '07-Oct-2026 10:00',
    samplingLocation: 'Packing Hall B',
    quantitySampled: '60 g',
    quantityUsedForTest: '10 g',
    testMethod: 'Pour Plate',
    dilutionFactor: '1:10 (10g in 90ml Buffered Peptone Water)',
    diluentType: 'Buffered Peptone Water pH 7.0',
    incubatorChamber: 'Incubator INC-MB-01 (Chamber B)',
    targetIncubationTemp: '30.0°C – 35.0°C',
    actualIncubationTemp: '32.1°C',
    incubationDuration: '3 Days (72h)',
    incubationStartTime: '07-Oct-2026 11:00 AM',
    expectedReadingDate: '10-Oct-2026 11:00 AM',
    completionDate: '12-Oct-2026 14:00',
    plateReadings: [
      { plateNo: 1, serialNo: 'PLT-TSA-261007-01', cfu: 18, morphology: 'Smooth creamy colonies', readingTimestamp: '10-Oct-2026 11:15 AM' },
      { plateNo: 2, serialNo: 'PLT-TSA-261007-02', cfu: 22, morphology: 'Smooth creamy colonies', readingTimestamp: '10-Oct-2026 11:15 AM' },
      { plateNo: 3, serialNo: 'PLT-TSA-261007-03', cfu: 20, morphology: 'Smooth creamy colonies', readingTimestamp: '10-Oct-2026 11:15 AM' },
      { plateNo: 4, serialNo: 'PLT-TSA-261007-04', cfu: 19, morphology: 'Smooth creamy colonies', readingTimestamp: '10-Oct-2026 11:15 AM' },
      { plateNo: 5, serialNo: 'PLT-TSA-261007-05', cfu: 21, morphology: 'Smooth creamy colonies', readingTimestamp: '10-Oct-2026 11:15 AM' }
    ],
    negativeControlCfu: 0,
    positiveControlVerified: true,
    averageCfuPerPlate: 20.0,
    calculatedBioburden: '200 CFU/g (2.0 × 10² CFU/g)',
    acceptanceLimit: 'NMT 1000 CFU/g (USP <1111>)',
    complianceStatus: 'Pass',
    specifiedOrganisms: [
      { organismName: 'Escherichia coli', selectiveMedium: 'MacConkey Broth / Agar', incubationCondition: '42°C – 44°C, 24h', colonyObservation: 'No growth / Negative', result: 'Absent', complianceStatus: 'Complies' },
      { organismName: 'Salmonella species', selectiveMedium: 'RVS Broth / XLD Agar', incubationCondition: '30°C – 35°C, 24h', colonyObservation: 'No red colonies with black centers', result: 'Absent', complianceStatus: 'Complies' }
    ],
    remarks: 'Total Aerobic Microbial Count is well within the specified pharmacopoeial acceptance limits. Test complies with USP <61> / <62>.',
    analystSignOff: {
      name: 'Dr. Sarah Jenkins',
      role: 'Senior QC Microbiologist',
      signedAt: '12-Oct-2026 14:15'
    },
    qcReviewSignOff: {
      name: 'Emily Watson',
      role: 'QC Laboratory Manager',
      status: 'Approved',
      reviewedAt: '12-Oct-2026 16:30'
    }
  },

  // 8. Completed - Row 8 in screenshot
  {
    id: 'lab-8',
    sNo: 8,
    mltTestId: 'MLT-26-27-000128',
    sampleId: 'SAM-26-27-000461',
    sampleType: 'Finished Product',
    productOrMaterial: 'Product B',
    testParameter: 'TYMC',
    medium: 'SDA',
    numberOfPlates: 5,
    status: 'Completed',
    assignedDate: '12-Oct-2026',
    batchNo: 'BAT-PRDB-261005',
    productCode: 'PRD-CAP-250',
    arNumber: 'AR/26-27/MLT/0495',
    productionSchedule: 'SCH-2026-0966',
    scheduleLine: 'Line 4 - Capsule Encapsulation Suite',
    assignedAnalyst: 'Dr. Sarah Jenkins',
    samplingDate: '07-Oct-2026 10:00',
    samplingLocation: 'Packing Hall B',
    quantitySampled: '60 g',
    quantityUsedForTest: '10 g',
    testMethod: 'Pour Plate',
    dilutionFactor: '1:10 (10g in 90ml Buffered Peptone Water)',
    diluentType: 'Buffered Peptone Water pH 7.0',
    incubatorChamber: 'Incubator INC-MB-03 (Chamber C)',
    targetIncubationTemp: '20.0°C – 25.0°C',
    actualIncubationTemp: '22.4°C',
    incubationDuration: '5 Days (120h)',
    incubationStartTime: '07-Oct-2026 11:30 AM',
    expectedReadingDate: '12-Oct-2026 11:30 AM',
    completionDate: '12-Oct-2026 14:30',
    plateReadings: [
      { plateNo: 1, serialNo: 'PLT-SDA-261007-11', cfu: 2, morphology: 'Small convex yeast colonies', readingTimestamp: '12-Oct-2026 11:45 AM' },
      { plateNo: 2, serialNo: 'PLT-SDA-261007-12', cfu: 3, morphology: 'Small convex yeast colonies', readingTimestamp: '12-Oct-2026 11:45 AM' },
      { plateNo: 3, serialNo: 'PLT-SDA-261007-13', cfu: 1, morphology: 'Small convex yeast colonies', readingTimestamp: '12-Oct-2026 11:45 AM' },
      { plateNo: 4, serialNo: 'PLT-SDA-261007-14', cfu: 2, morphology: 'Small convex yeast colonies', readingTimestamp: '12-Oct-2026 11:45 AM' },
      { plateNo: 5, serialNo: 'PLT-SDA-261007-15', cfu: 2, morphology: 'Small convex yeast colonies', readingTimestamp: '12-Oct-2026 11:45 AM' }
    ],
    negativeControlCfu: 0,
    positiveControlVerified: true,
    averageCfuPerPlate: 2.0,
    calculatedBioburden: '20 CFU/g (2.0 × 10¹ CFU/g)',
    acceptanceLimit: 'NMT 100 CFU/g (USP <1111>)',
    complianceStatus: 'Pass',
    remarks: 'Total Yeast and Mold Count within validated acceptance limits. Negative control showed no growth.',
    analystSignOff: {
      name: 'Dr. Sarah Jenkins',
      role: 'Senior QC Microbiologist',
      signedAt: '12-Oct-2026 14:40'
    },
    qcReviewSignOff: {
      name: 'Emily Watson',
      role: 'QC Laboratory Manager',
      status: 'Approved',
      reviewedAt: '12-Oct-2026 16:35'
    }
  },

  // 9. Pending (To Be Tested) - Row 9 in screenshot
  {
    id: 'lab-9',
    sNo: 9,
    mltTestId: 'MLT-26-27-000129',
    sampleId: 'SAM-26-27-000462',
    sampleType: 'Water Sample',
    productOrMaterial: 'WFI',
    testParameter: 'TAMC',
    medium: 'SCDA (TSA)',
    numberOfPlates: 2,
    status: 'Pending',
    assignedDate: '13-Oct-2026',
    batchNo: 'WTR-WFI-261013',
    productCode: 'UTL-WFI-002',
    arNumber: 'AR/26-27/MLT/0496',
    productionSchedule: 'SCH-2026-0967',
    scheduleLine: 'WFI Generation Loop 2 - Sampling Point 12',
    assignedAnalyst: 'Michael Chen',
    samplingDate: '13-Oct-2026 06:30',
    samplingLocation: 'Injectable Formulation Suite WFI Point',
    quantitySampled: '500 ml',
    quantityUsedForTest: '100 ml',
    testMethod: 'Membrane Filtration',
    dilutionFactor: 'Direct (100ml through 0.45µm membrane)',
    diluentType: 'None (Direct filtration)',
    targetIncubationTemp: '30.0°C – 35.0°C',
    incubationDuration: '5 Days (120h)',
    plateReadings: [
      { plateNo: 1, serialNo: 'PLT-TSA-261013-01', cfu: '—' },
      { plateNo: 2, serialNo: 'PLT-TSA-261013-02', cfu: '—' }
    ],
    negativeControlCfu: 0,
    positiveControlVerified: true,
    acceptanceLimit: 'NMT 10 CFU/100ml (USP Monograph for WFI)',
    remarks: 'Aseptic filtration setup verified. Sterility of filtration manifold verified prior to filtration.'
  },

  // 10. In Progress - Row 10 in screenshot
  {
    id: 'lab-10',
    sNo: 10,
    mltTestId: 'MLT-26-27-000130',
    sampleId: 'SAM-26-27-000463',
    sampleType: 'Finished Product',
    productOrMaterial: 'Product C',
    testParameter: 'TYMC',
    medium: 'SDA',
    numberOfPlates: 5,
    status: 'In Progress',
    assignedDate: '13-Oct-2026',
    batchNo: 'BAT-PRDC-261009',
    productCode: 'PRD-SYR-050',
    arNumber: 'AR/26-27/MLT/0497',
    productionSchedule: 'SCH-2026-0968',
    scheduleLine: 'Liquid Oral Suspension Line 2',
    assignedAnalyst: 'Dr. Sarah Jenkins',
    samplingDate: '12-Oct-2026 15:00',
    samplingLocation: 'Syrup Filling Suite Booth 1',
    quantitySampled: '100 ml',
    quantityUsedForTest: '10 ml',
    testMethod: 'Pour Plate',
    dilutionFactor: '1:10 (10ml in 90ml Diluent)',
    diluentType: 'Buffered Sodium Chloride-Peptone Solution pH 7.0',
    incubatorChamber: 'Incubator INC-MB-03 (Chamber A)',
    targetIncubationTemp: '20.0°C – 25.0°C',
    actualIncubationTemp: '22.2°C',
    incubationDuration: '5 to 7 Days (120h – 168h)',
    incubationStartTime: '13-Oct-2026 09:30 AM',
    expectedReadingDate: '18-Oct-2026 09:30 AM',
    plateReadings: [
      { plateNo: 1, serialNo: 'PLT-SDA-261013-11', cfu: 1, morphology: 'Single mold filament developing', readingTimestamp: '15-Oct-2026 09:30 AM' },
      { plateNo: 2, serialNo: 'PLT-SDA-261013-12', cfu: 0, morphology: 'No growth at 48h', readingTimestamp: '15-Oct-2026 09:30 AM' },
      { plateNo: 3, serialNo: 'PLT-SDA-261013-13', cfu: 1, morphology: 'Single mold filament developing', readingTimestamp: '15-Oct-2026 09:30 AM' },
      { plateNo: 4, serialNo: 'PLT-SDA-261013-14', cfu: 0, morphology: 'No growth at 48h', readingTimestamp: '15-Oct-2026 09:30 AM' },
      { plateNo: 5, serialNo: 'PLT-SDA-261013-15', cfu: 2, morphology: 'Small white colonies', readingTimestamp: '15-Oct-2026 09:30 AM' }
    ],
    negativeControlCfu: 0,
    positiveControlVerified: true,
    averageCfuPerPlate: 0.8,
    calculatedBioburden: '~8 CFU/ml (Interim 48h count)',
    acceptanceLimit: 'NMT 100 CFU/ml (USP <1111>)',
    complianceStatus: 'Pending',
    remarks: 'Incubation in progress. Temperature logged within 22.0°C - 22.5°C.'
  },

  // 11. Pending (To Be Tested) #7
  {
    id: 'lab-11',
    sNo: 11,
    mltTestId: 'MLT-26-27-000131',
    sampleId: 'SAM-26-27-000464',
    sampleType: 'In-process Material',
    productOrMaterial: 'Core Tablet Blend D',
    testParameter: 'TAMC',
    medium: 'SCDA (TSA)',
    numberOfPlates: 5,
    status: 'Pending',
    assignedDate: '14-Oct-2026',
    batchNo: 'BAT-CTD-261014',
    productCode: 'INP-BLD-015',
    arNumber: 'AR/26-27/MLT/0498',
    productionSchedule: 'SCH-2026-0969',
    scheduleLine: 'Direct Compression Suite B',
    assignedAnalyst: 'Robert Taylor',
    samplingDate: '14-Oct-2026 08:00',
    samplingLocation: 'Blender Bin Discharge Valve',
    quantitySampled: '40 g',
    quantityUsedForTest: '10 g',
    testMethod: 'Pour Plate',
    dilutionFactor: '1:10 (10g in 90ml Buffered Peptone Water)',
    diluentType: 'Buffered Peptone Water pH 7.0',
    targetIncubationTemp: '30.0°C – 35.0°C',
    incubationDuration: '3 to 5 Days (72h – 120h)',
    plateReadings: [
      { plateNo: 1, serialNo: 'PLT-TSA-261014-01', cfu: '—' },
      { plateNo: 2, serialNo: 'PLT-TSA-261014-02', cfu: '—' },
      { plateNo: 3, serialNo: 'PLT-TSA-261014-03', cfu: '—' },
      { plateNo: 4, serialNo: 'PLT-TSA-261014-04', cfu: '—' },
      { plateNo: 5, serialNo: 'PLT-TSA-261014-05', cfu: '—' }
    ],
    negativeControlCfu: 0,
    positiveControlVerified: true,
    acceptanceLimit: 'NMT 1000 CFU/g (In-process Specification)',
    remarks: 'Sample received in double poly bag with tamper evident seal.'
  },

  // 12. Pending (To Be Tested) #8
  {
    id: 'lab-12',
    sNo: 12,
    mltTestId: 'MLT-26-27-000132',
    sampleId: 'SAM-26-27-000465',
    sampleType: 'Raw Material',
    productOrMaterial: 'Microcrystalline Cellulose PH102',
    testParameter: 'TAMC',
    medium: 'SCDA (TSA)',
    numberOfPlates: 3,
    status: 'Pending',
    assignedDate: '14-Oct-2026',
    batchNo: 'LOT-MCC-261002',
    productCode: 'RAW-EXC-005',
    arNumber: 'AR/26-27/MLT/0499',
    productionSchedule: 'SCH-2026-0970',
    scheduleLine: 'QC Raw Material Testing Station 1',
    assignedAnalyst: 'Michael Chen',
    samplingDate: '13-Oct-2026 14:00',
    samplingLocation: 'Warehouse Sampling Booth C',
    quantitySampled: '50 g',
    quantityUsedForTest: '10 g',
    testMethod: 'Pour Plate',
    dilutionFactor: '1:10 (10g in 90ml Diluent)',
    diluentType: 'Phosphate Buffer pH 7.2',
    targetIncubationTemp: '30.0°C – 35.0°C',
    incubationDuration: '3 to 5 Days (72h – 120h)',
    plateReadings: [
      { plateNo: 1, serialNo: 'PLT-TSA-261014-11', cfu: '—' },
      { plateNo: 2, serialNo: 'PLT-TSA-261014-12', cfu: '—' },
      { plateNo: 3, serialNo: 'PLT-TSA-261014-13', cfu: '—' }
    ],
    negativeControlCfu: 0,
    positiveControlVerified: true,
    acceptanceLimit: 'NMT 1000 CFU/g (USP/NF)',
    remarks: 'Inoculation medium pre-warmed to 45°C.'
  },

  // 13. Pending (To Be Tested) #9
  {
    id: 'lab-13',
    sNo: 13,
    mltTestId: 'MLT-26-27-000133',
    sampleId: 'SAM-26-27-000466',
    sampleType: 'Raw Material',
    productOrMaterial: 'Microcrystalline Cellulose PH102',
    testParameter: 'TYMC',
    medium: 'SDA',
    numberOfPlates: 3,
    status: 'Pending',
    assignedDate: '14-Oct-2026',
    batchNo: 'LOT-MCC-261002',
    productCode: 'RAW-EXC-005',
    arNumber: 'AR/26-27/MLT/0500',
    productionSchedule: 'SCH-2026-0970',
    scheduleLine: 'QC Raw Material Testing Station 1',
    assignedAnalyst: 'Michael Chen',
    samplingDate: '13-Oct-2026 14:00',
    samplingLocation: 'Warehouse Sampling Booth C',
    quantitySampled: '50 g',
    quantityUsedForTest: '10 g',
    testMethod: 'Pour Plate',
    dilutionFactor: '1:10 (10g in 90ml Diluent)',
    diluentType: 'Phosphate Buffer pH 7.2',
    targetIncubationTemp: '20.0°C – 25.0°C',
    incubationDuration: '5 to 7 Days (120h – 168h)',
    plateReadings: [
      { plateNo: 1, serialNo: 'PLT-SDA-261014-21', cfu: '—' },
      { plateNo: 2, serialNo: 'PLT-SDA-261014-22', cfu: '—' },
      { plateNo: 3, serialNo: 'PLT-SDA-261014-23', cfu: '—' }
    ],
    negativeControlCfu: 0,
    positiveControlVerified: true,
    acceptanceLimit: 'NMT 100 CFU/g (USP/NF)',
    remarks: 'SDA media plate lot checked for growth promotion.'
  },

  // 14. Pending (To Be Tested) #10
  {
    id: 'lab-14',
    sNo: 14,
    mltTestId: 'MLT-26-27-000134',
    sampleId: 'SAM-26-27-000467',
    sampleType: 'Finished Product',
    productOrMaterial: 'Oral Rehydration Salts',
    testParameter: 'TAMC',
    medium: 'SCDA (TSA)',
    numberOfPlates: 5,
    status: 'Pending',
    assignedDate: '15-Oct-2026',
    batchNo: 'BAT-ORS-261015',
    productCode: 'PRD-ORS-020',
    arNumber: 'AR/26-27/MLT/0501',
    productionSchedule: 'SCH-2026-0971',
    scheduleLine: 'Sachet Packing Line 1',
    assignedAnalyst: 'Dr. Sarah Jenkins',
    samplingDate: '15-Oct-2026 09:00',
    samplingLocation: 'Sachet Packing Secondary Suite',
    quantitySampled: '100 g',
    quantityUsedForTest: '10 g',
    testMethod: 'Pour Plate',
    dilutionFactor: '1:10 (10g in 90ml Buffered Peptone Water)',
    diluentType: 'Buffered Peptone Water pH 7.0',
    targetIncubationTemp: '30.0°C – 35.0°C',
    incubationDuration: '3 to 5 Days (72h – 120h)',
    plateReadings: [
      { plateNo: 1, serialNo: 'PLT-TSA-261015-01', cfu: '—' },
      { plateNo: 2, serialNo: 'PLT-TSA-261015-02', cfu: '—' },
      { plateNo: 3, serialNo: 'PLT-TSA-261015-03', cfu: '—' },
      { plateNo: 4, serialNo: 'PLT-TSA-261015-04', cfu: '—' },
      { plateNo: 5, serialNo: 'PLT-TSA-261015-05', cfu: '—' }
    ],
    negativeControlCfu: 0,
    positiveControlVerified: true,
    acceptanceLimit: 'NMT 1000 CFU/g (USP <1111>)',
    remarks: 'Awaiting analyst preparation.'
  },

  // 15. Pending (To Be Tested) #11
  {
    id: 'lab-15',
    sNo: 15,
    mltTestId: 'MLT-26-27-000135',
    sampleId: 'SAM-26-27-000468',
    sampleType: 'Water Sample',
    productOrMaterial: 'Purified Water',
    testParameter: 'TAMC',
    medium: 'SCDA (TSA)',
    numberOfPlates: 2,
    status: 'Pending',
    assignedDate: '15-Oct-2026',
    batchNo: 'WTR-PW-261015',
    productCode: 'UTL-WTR-001',
    arNumber: 'AR/26-27/MLT/0502',
    productionSchedule: 'SCH-2026-0972',
    scheduleLine: 'Loop 2 - Quality Control Lab Sink Valve W-201',
    assignedAnalyst: 'Michael Chen',
    samplingDate: '15-Oct-2026 08:15',
    samplingLocation: 'Microbiology Preparation Room Tap',
    quantitySampled: '500 ml',
    quantityUsedForTest: '100 ml',
    testMethod: 'Membrane Filtration',
    dilutionFactor: 'Direct (100ml)',
    diluentType: 'None',
    targetIncubationTemp: '30.0°C – 35.0°C',
    incubationDuration: '5 Days (120h)',
    plateReadings: [
      { plateNo: 1, serialNo: 'PLT-TSA-261015-11', cfu: '—' },
      { plateNo: 2, serialNo: 'PLT-TSA-261015-12', cfu: '—' }
    ],
    negativeControlCfu: 0,
    positiveControlVerified: true,
    acceptanceLimit: 'NMT 100 CFU/ml (USP Monograph)',
    remarks: 'Daily routine point sampling.'
  },

  // 16. Pending (To Be Tested) #12
  {
    id: 'lab-16',
    sNo: 16,
    mltTestId: 'MLT-26-27-000136',
    sampleId: 'SAM-26-27-000469',
    sampleType: 'Finished Product',
    productOrMaterial: 'Paracetamol Oral Suspension 120mg/5ml',
    testParameter: 'TAMC',
    medium: 'SCDA (TSA)',
    numberOfPlates: 5,
    status: 'Pending',
    assignedDate: '16-Oct-2026',
    batchNo: 'BAT-PCM-261016',
    productCode: 'PRD-SYR-120',
    arNumber: 'AR/26-27/MLT/0503',
    productionSchedule: 'SCH-2026-0973',
    scheduleLine: 'Liquid Oral Suspension Line 1',
    assignedAnalyst: 'Dr. Sarah Jenkins',
    samplingDate: '16-Oct-2026 09:30',
    samplingLocation: 'Dispensing & Blending Suite 4',
    quantitySampled: '150 ml',
    quantityUsedForTest: '10 ml',
    testMethod: 'Pour Plate',
    dilutionFactor: '1:10 (10ml in 90ml Diluent)',
    diluentType: 'Fluid Casein Digest-Soy Peptone Medium',
    targetIncubationTemp: '30.0°C – 35.0°C',
    incubationDuration: '3 to 5 Days (72h – 120h)',
    plateReadings: [
      { plateNo: 1, serialNo: 'PLT-TSA-261016-01', cfu: '—' },
      { plateNo: 2, serialNo: 'PLT-TSA-261016-02', cfu: '—' },
      { plateNo: 3, serialNo: 'PLT-TSA-261016-03', cfu: '—' },
      { plateNo: 4, serialNo: 'PLT-TSA-261016-04', cfu: '—' },
      { plateNo: 5, serialNo: 'PLT-TSA-261016-05', cfu: '—' }
    ],
    negativeControlCfu: 0,
    positiveControlVerified: true,
    acceptanceLimit: 'NMT 100 CFU/ml (USP <1111>)',
    remarks: 'Ready for test execution.'
  },

  // 17. In Progress #3
  {
    id: 'lab-17',
    sNo: 17,
    mltTestId: 'MLT-26-27-000137',
    sampleId: 'SAM-26-27-000470',
    sampleType: 'In-process Material',
    productOrMaterial: 'Aqueous Coating Solution A',
    testParameter: 'TAMC',
    medium: 'SCDA (TSA)',
    numberOfPlates: 5,
    status: 'In Progress',
    assignedDate: '13-Oct-2026',
    batchNo: 'BAT-COAT-261013',
    productCode: 'INP-SOL-009',
    arNumber: 'AR/26-27/MLT/0504',
    productionSchedule: 'SCH-2026-0974',
    scheduleLine: 'Auto-Coater Preparation Room 105',
    assignedAnalyst: 'Robert Taylor',
    samplingDate: '13-Oct-2026 11:00',
    samplingLocation: 'Preparation Tank 2 Outlet',
    quantitySampled: '100 ml',
    quantityUsedForTest: '10 ml',
    testMethod: 'Pour Plate',
    dilutionFactor: '1:10 (10ml in 90ml Diluent)',
    diluentType: 'Buffered Peptone Water pH 7.0',
    incubatorChamber: 'Incubator INC-MB-02 (Chamber B)',
    targetIncubationTemp: '30.0°C – 35.0°C',
    actualIncubationTemp: '32.5°C',
    incubationDuration: '3 to 5 Days (72h – 120h)',
    incubationStartTime: '13-Oct-2026 13:00',
    expectedReadingDate: '16-Oct-2026 13:00',
    plateReadings: [
      { plateNo: 1, serialNo: 'PLT-TSA-261013-31', cfu: 8, morphology: 'Small circular colonies', readingTimestamp: '15-Oct-2026 13:00' },
      { plateNo: 2, serialNo: 'PLT-TSA-261013-32', cfu: 10, morphology: 'Small circular colonies', readingTimestamp: '15-Oct-2026 13:00' },
      { plateNo: 3, serialNo: 'PLT-TSA-261013-33', cfu: 9, morphology: 'Small circular colonies', readingTimestamp: '15-Oct-2026 13:00' },
      { plateNo: 4, serialNo: 'PLT-TSA-261013-34', cfu: 11, morphology: 'Small circular colonies', readingTimestamp: '15-Oct-2026 13:00' },
      { plateNo: 5, serialNo: 'PLT-TSA-261013-35', cfu: 9, morphology: 'Small circular colonies', readingTimestamp: '15-Oct-2026 13:00' }
    ],
    negativeControlCfu: 0,
    positiveControlVerified: true,
    averageCfuPerPlate: 9.4,
    calculatedBioburden: '~94 CFU/ml (Interim 48h count)',
    acceptanceLimit: 'NMT 500 CFU/ml (In-process Spec)',
    complianceStatus: 'Pending',
    remarks: '48h interim inspection shows normal count well below limits.'
  },

  // 18. In Progress #4
  {
    id: 'lab-18',
    sNo: 18,
    mltTestId: 'MLT-26-27-000138',
    sampleId: 'SAM-26-27-000471',
    sampleType: 'Raw Material',
    productOrMaterial: 'Povidone K30 USP',
    testParameter: 'TAMC',
    medium: 'SCDA (TSA)',
    numberOfPlates: 3,
    status: 'In Progress',
    assignedDate: '13-Oct-2026',
    batchNo: 'LOT-PVDK-260991',
    productCode: 'RAW-EXC-021',
    arNumber: 'AR/26-27/MLT/0505',
    productionSchedule: 'SCH-2026-0975',
    scheduleLine: 'QC Raw Material Testing Station 2',
    assignedAnalyst: 'Michael Chen',
    samplingDate: '13-Oct-2026 13:30',
    samplingLocation: 'Warehouse Sampling Booth B',
    quantitySampled: '30 g',
    quantityUsedForTest: '10 g',
    testMethod: 'Pour Plate',
    dilutionFactor: '1:10 (10g in 90ml Diluent)',
    diluentType: 'Buffered Peptone Water pH 7.0',
    incubatorChamber: 'Incubator INC-MB-01 (Chamber A)',
    targetIncubationTemp: '30.0°C – 35.0°C',
    actualIncubationTemp: '32.2°C',
    incubationDuration: '3 to 5 Days (72h – 120h)',
    incubationStartTime: '13-Oct-2026 15:30',
    expectedReadingDate: '16-Oct-2026 15:30',
    plateReadings: [
      { plateNo: 1, serialNo: 'PLT-TSA-261013-41', cfu: 24, morphology: 'Pale yellow colonies', readingTimestamp: '15-Oct-2026 15:30' },
      { plateNo: 2, serialNo: 'PLT-TSA-261013-42', cfu: 28, morphology: 'Pale yellow colonies', readingTimestamp: '15-Oct-2026 15:30' },
      { plateNo: 3, serialNo: 'PLT-TSA-261013-43', cfu: 25, morphology: 'Pale yellow colonies', readingTimestamp: '15-Oct-2026 15:30' }
    ],
    negativeControlCfu: 0,
    positiveControlVerified: true,
    averageCfuPerPlate: 25.7,
    calculatedBioburden: '~257 CFU/g (Interim count)',
    acceptanceLimit: 'NMT 1000 CFU/g (USP Monograph)',
    complianceStatus: 'Pending',
    remarks: 'Incubation progressing under controlled temperature.'
  }
];

// Helper to generate the remaining 26 Completed records so total Completed = 28
const generateCompletedRecords = (): MLTLabTestRecord[] => {
  const materials = [
    { name: 'Amoxicillin Trihydrate Powder', type: 'Raw Material' as const, param: 'TAMC' as const, med: 'SCDA (TSA)', limit: 'NMT 1000 CFU/g' },
    { name: 'Amoxicillin Trihydrate Powder', type: 'Raw Material' as const, param: 'TYMC' as const, med: 'SDA', limit: 'NMT 100 CFU/g' },
    { name: 'Cough Syrup Formula 2', type: 'Finished Product' as const, param: 'TAMC' as const, med: 'SCDA (TSA)', limit: 'NMT 100 CFU/ml' },
    { name: 'Cough Syrup Formula 2', type: 'Finished Product' as const, param: 'TYMC' as const, med: 'SDA', limit: 'NMT 10 CFU/ml' },
    { name: 'Water for Injection Loop 1', type: 'Water Sample' as const, param: 'TAMC' as const, med: 'SCDA (TSA)', limit: 'NMT 10 CFU/100ml' },
    { name: 'Purified Water Point P-08', type: 'Water Sample' as const, param: 'TAMC' as const, med: 'SCDA (TSA)', limit: 'NMT 100 CFU/ml' },
    { name: 'Ibuprofen Suspension 100mg/5ml', type: 'Finished Product' as const, param: 'TAMC' as const, med: 'SCDA (TSA)', limit: 'NMT 100 CFU/ml' },
    { name: 'Ibuprofen Suspension 100mg/5ml', type: 'Finished Product' as const, param: 'TYMC' as const, med: 'SDA', limit: 'NMT 10 CFU/ml' },
    { name: 'Starch Paste Binder 10%', type: 'In-process Material' as const, param: 'TAMC' as const, med: 'SCDA (TSA)', limit: 'NMT 500 CFU/ml' },
    { name: 'Magnesium Stearate EP/BP', type: 'Raw Material' as const, param: 'TAMC' as const, med: 'SCDA (TSA)', limit: 'NMT 1000 CFU/g' },
    { name: 'Magnesium Stearate EP/BP', type: 'Raw Material' as const, param: 'TYMC' as const, med: 'SDA', limit: 'NMT 100 CFU/g' },
    { name: 'Lactose Monohydrate', type: 'Raw Material' as const, param: 'TAMC' as const, med: 'SCDA (TSA)', limit: 'NMT 1000 CFU/g' },
    { name: 'Lactose Monohydrate', type: 'Raw Material' as const, param: 'TYMC' as const, med: 'SDA', limit: 'NMT 100 CFU/g' }
  ];

  const extras: MLTLabTestRecord[] = [];
  let sNoCounter = 19;

  materials.forEach((mat, idx) => {
    // Generate 2 records per material entry to reach 26 additional completed records
    for (let run = 1; run <= 2; run++) {
      const recId = `lab-c-${idx}-${run}`;
      const testNum = 139 + extras.length;
      const sampNum = 472 + extras.length;
      const dateOffset = 1 + (idx % 8);
      const assignDay = dateOffset < 10 ? `0${dateOffset}` : `${dateOffset}`;

      extras.push({
        id: recId,
        sNo: sNoCounter++,
        mltTestId: `MLT-26-27-000${testNum}`,
        sampleId: `SAM-26-27-000${sampNum}`,
        sampleType: mat.type,
        productOrMaterial: mat.name,
        testParameter: mat.param,
        medium: mat.med,
        numberOfPlates: mat.type === 'Water Sample' ? 2 : (mat.type === 'Raw Material' ? 3 : 5),
        status: 'Completed',
        assignedDate: `${assignDay}-Oct-2026`,
        batchNo: `BAT-GEN-2610${assignDay}`,
        productCode: `PRD-${mat.type.substring(0, 3).toUpperCase()}-${100 + idx}`,
        arNumber: `AR/26-27/MLT/05${10 + extras.length}`,
        productionSchedule: `SCH-2026-09${80 + idx}`,
        scheduleLine: 'Routine QC Testing Station',
        assignedAnalyst: idx % 2 === 0 ? 'Dr. Sarah Jenkins' : 'Michael Chen',
        samplingDate: `${assignDay}-Oct-2026 09:00`,
        samplingLocation: 'QC Sampling Area 1',
        quantitySampled: mat.type === 'Water Sample' ? '500 ml' : '50 g',
        quantityUsedForTest: mat.type === 'Water Sample' ? '100 ml' : '10 g',
        testMethod: mat.type === 'Water Sample' ? 'Membrane Filtration' : 'Pour Plate',
        dilutionFactor: mat.type === 'Water Sample' ? 'Direct (100ml)' : '1:10 (10g in 90ml Buffered Peptone Water)',
        diluentType: 'Buffered Peptone Water pH 7.0',
        incubatorChamber: mat.param === 'TAMC' ? 'Incubator INC-MB-01 (Chamber A)' : 'Incubator INC-MB-03 (Chamber B)',
        targetIncubationTemp: mat.param === 'TAMC' ? '30.0°C – 35.0°C' : '20.0°C – 25.0°C',
        actualIncubationTemp: mat.param === 'TAMC' ? '32.3°C' : '22.4°C',
        incubationDuration: mat.param === 'TAMC' ? '3 Days (72h)' : '5 Days (120h)',
        incubationStartTime: `${assignDay}-Oct-2026 10:00`,
        expectedReadingDate: `${dateOffset + 3}-Oct-2026 10:00`,
        completionDate: `${dateOffset + 3}-Oct-2026 15:00`,
        plateReadings: [
          { plateNo: 1, serialNo: `PLT-${assignDay}-01`, cfu: 6 + (idx % 5), morphology: 'Normal bacterial colonies', readingTimestamp: `${dateOffset + 3}-Oct-2026` },
          { plateNo: 2, serialNo: `PLT-${assignDay}-02`, cfu: 8 + (idx % 4), morphology: 'Normal bacterial colonies', readingTimestamp: `${dateOffset + 3}-Oct-2026` },
          { plateNo: 3, serialNo: `PLT-${assignDay}-03`, cfu: 7 + (idx % 6), morphology: 'Normal bacterial colonies', readingTimestamp: `${dateOffset + 3}-Oct-2026` }
        ],
        negativeControlCfu: 0,
        positiveControlVerified: true,
        averageCfuPerPlate: 7.5,
        calculatedBioburden: mat.type === 'Water Sample' ? '< 10 CFU/100ml' : '75 CFU/g',
        acceptanceLimit: mat.limit,
        complianceStatus: 'Pass',
        remarks: 'Test executed in compliance with validated laboratory SOP. Quality specifications met.',
        analystSignOff: {
          name: idx % 2 === 0 ? 'Dr. Sarah Jenkins' : 'Michael Chen',
          role: 'Senior QC Microbiologist',
          signedAt: `${dateOffset + 3}-Oct-2026 15:30`
        },
        qcReviewSignOff: {
          name: 'Emily Watson',
          role: 'QC Laboratory Manager',
          status: 'Approved',
          reviewedAt: `${dateOffset + 3}-Oct-2026 17:00`
        }
      });
    }
  });

  return extras;
};

export const ALL_MLT_LAB_TEST_RECORDS: MLTLabTestRecord[] = [
  ...INITIAL_MLT_LAB_TEST_RECORDS,
  ...generateCompletedRecords()
];
